// The install's phase list — the thing every progress surface counts against.
//
// This is a DECLARATION of what `installer/entrypoint.sh` emits today, not a
// proposal for what it should emit. The install-core contract lane declares the
// surface; it does not change install behaviour. Where the
// shipped numbering is odd, the oddity is recorded here as a fact and routed to
// the architect as a finding — see the notes on `converge` and on PHASE_TOTAL.

/** One phase the installer can report. */
export interface PhaseSpec {
  /** The exact string `entrypoint.sh` passes to `emit`. Matched literally by readers. */
  readonly name: string;
  /** `phaseIndex` the installer reports for this phase. */
  readonly index: number;
  /**
   * `false` when the phase is skipped on some supported path, so the reported
   * phase SET is smaller than this list. A progress surface that assumes every
   * phase arrives will stall on a run that legitimately skips one.
   */
  readonly always: boolean;
  /** Why a non-`always` phase can be absent. Empty for unconditional phases. */
  readonly skippedWhen?: string;
}

/**
 * `phaseTotal` as the installer reports it (`PHASE_TOTAL=9` in entrypoint.sh).
 *
 * Note it counts the terminal `done` emit as a phase, so a run reports
 * `9/9` at completion rather than `8/8`. Readers must take `phaseTotal` from
 * the document rather than from this constant — a container built at a
 * different commit may report a different total, and the document is the
 * authority about itself.
 */
export const REPORTED_PHASE_TOTAL = 9;

/**
 * The from-zero install, in the order `entrypoint.sh` emits them.
 *
 * `create-brains` is CONDITIONAL, and as of the platform-only skip path it is
 * conditional on the PAVED PATH — which is a change in kind, not just in
 * wording. `entrypoint.sh` skips it when `skips_github` holds, which is either
 * of two provenances:
 *
 *   SKIP_BRAINS=true      a test/CI rig, as before;
 *   GITHUB_DECLINED=true  a FOUNDER who took the install page's skip click.
 *
 * So a supported, founder-driven install now reports 6 → 8 and never emits
 * index 7. Any surface that counts arrivals, or draws a bar from the phase
 * list, must tolerate its absence rather than wait for it — previously that was
 * a robustness requirement met only by rigs, and it is now load-bearing for
 * real founders.
 *
 * THERE IS NO `skipped` STATUS TO EMIT. The status document's `status` is a
 * closed `running | done | failed` and v1 is frozen, so absence is the whole of
 * how a skipped phase is represented there; emitting `done` for work that never
 * ran would put a falsehood on the founder's receipt. The local event envelope
 * (`events.ts`) does carry a terminal `skipped`, and that is where the
 * distinction survives for a consumer that needs it.
 */
export const INSTALL_PHASES: readonly PhaseSpec[] = [
  { name: "preflight", index: 1, always: true },
  { name: "foundry-create", index: 2, always: true },
  { name: "deploy", index: 3, always: true },
  { name: "verify-health", index: 4, always: true },
  { name: "deploy-model", index: 5, always: true },
  { name: "deploy-advisors", index: 6, always: true },
  {
    name: "create-brains",
    index: 7,
    always: false,
    skippedWhen: "skips_github — either SKIP_BRAINS=true (test and CI rigs) or GITHUB_DECLINED=true (a founder declined the GitHub step on the install page, which is a SUPPORTED paved-path install that completes platform-only)",
  },
  { name: "a2a-wire", index: 8, always: true },
];

/**
 * The terminal phase name. `entrypoint.sh` emits `emit done done "$PHASE_TOTAL"`,
 * so the phase string and the status string are both the literal "done".
 */
export const TERMINAL_PHASE = "done";

/**
 * Update (converge) mode's only phase. The updater Container Apps Job runs the
 * SAME entrypoint with UPDATE_MODE=true, short-circuits before the from-zero
 * phases, and emits `converge` at index 1 — while PHASE_TOTAL is still 9.
 *
 * KNOWN ODDITY, declared rather than corrected: a COMPLETED converge
 * therefore reports `done` at phaseIndex 1 of phaseTotal 9. Any surface that
 * renders phaseIndex/phaseTotal as a fraction will show a finished update at
 * 11%. Surfaces should branch on `status === "done"` rather than on the
 * fraction. Recorded so the update and desktop surfaces do not rediscover it.
 */
export const CONVERGE_PHASE = "converge";

/**
 * The phase name the exit trap falls back to when it cannot tell where it died.
 *
 * `lib.sh` writes `${__CUR_PHASE:-unknown}`. Unreachable in practice — the
 * variable is initialised to `preflight` when the library is sourced — but it is
 * in the writer, so a reader switching exhaustively over phase names should
 * know it exists rather than meet it in production.
 */
export const UNKNOWN_PHASE = "unknown";

/**
 * The phase slug the exit trap sends on the WIRE when it cannot tell where it
 * died — `${__CUR_PHASE:-other}`, which differs from the status document's
 * `${__CUR_PHASE:-unknown}` fallback for the same condition.
 *
 * Both are unreachable in practice, for the same reason: the variable is
 * initialised when the library is sourced. Declared because a package whose job
 * is documenting this surface should not leave a reader to discover that the
 * two transports spell the same fallback differently.
 */
export const WIRE_UNKNOWN_PHASE = "other";

/** Every phase string a status document may legitimately carry. */
export const ALL_PHASE_NAMES: readonly string[] = [
  ...INSTALL_PHASES.map((p) => p.name),
  TERMINAL_PHASE,
  CONVERGE_PHASE,
  UNKNOWN_PHASE,
];

/** The phases a from-zero install reports, as a set — the weights table's subject. */
export const INSTALL_PHASE_NAMES: readonly string[] = INSTALL_PHASES.map((p) => p.name);
